import {
  useState,
  useMemo,
  useRef,
  useCallback,
  createContext,
  useContext,
} from 'react';
import { T, useGT } from 'gt-react';
import { SystemEntity } from '@erp/shared-types';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { InvoiceDetails } from '@/components/accounts-payable/invoice-detail/invoice-details';
import { PurchaseOrderDetails } from '@/components/accounts-payable/purchase-order-detail/purchase-order-details';
import {
  PaymentDetails,
  PaymentDetailsSkeleton,
} from '@/components/accounts-payable/payment-detail/payment-details';
import { SalesOrderDetails } from '@/components/accounts-receivable/sales-order-detail/sales-order-details';
import { ShipmentDetails } from '@/components/supply-chain/shipment-detail/shipment-details';
import { ARInvoiceDetails } from '@/components/accounts-receivable/invoice-detail/ar-invoice-details';
import { CashReceiptDetailView } from '@/components/accounts-receivable/cash-receipt-detail/cash-receipt-detail-view';
import { useQueries } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { skipToken } from '@tanstack/react-query';
import {
  CircleNotchIcon,
  EnvelopeSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { OutboundEmail } from '@erp/rpc';
import { RELEVANT_ENTITY_TYPES } from '@/api/rpc/hooks/core/useEntityAssociationsFiltered';
import { useDeleteEntityAssociation } from '@/api/rpc/hooks/core/useEntityAssociations';
import { Button } from '@/components/ui/button';
import { EmailThreadViewer } from '@/components/email-viewer/EmailThreadViewer';

function isRelevantEntityType(
  type: string,
): type is (typeof RELEVANT_ENTITY_TYPES)[number] {
  return RELEVANT_ENTITY_TYPES.some((entityType) => entityType === type);
}

/**
 * Loading spinner component for entity details
 */
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-full p-4">
      <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

/**
 * Error message component for entity details
 */
function ErrorMessage({ message }: { message: string }) {
  return <div className="p-4 text-sm text-muted-foreground">{message}</div>;
}

interface EntityTab {
  entityType: SystemEntity;
  entityId: string;
}

function getEntityKey(entityType: SystemEntity, entityId: string): string {
  return `${entityType}:${entityId}`;
}

interface EntityDetailTabsContextValue {
  entityArray: EntityTab[];
  activeTabId: string;
  setActiveTabId: (id: string) => void;
  renderEntityContent: (entity: EntityTab) => React.ReactNode;
  initialEntityKey: string;
  removeEntity: (entityType: SystemEntity, entityId: string) => void;
  isRemovingEntity: boolean;
  isConversationTab: (entityType: SystemEntity, entityId: string) => boolean;
  /**
   * True when this tab was surfaced from a parent entity's direct FK (e.g. a
   * PO's linked sales order) rather than a removable association row. The header
   * hides the unlink X for these.
   */
  isFkDerivedTab: (entityType: SystemEntity, entityId: string) => boolean;
  /**
   * True when the initial entity's related tabs are DERIVED on read (e.g. an
   * AR invoice) rather than backed by persisted association rows. Derived tabs
   * can't be removed — there is no row to delete — so the header hides the X.
   */
  associationsAreDerived: boolean;
}

const EntityDetailTabsContext =
  createContext<EntityDetailTabsContextValue | null>(null);

export function useEntityDetailTabsContext() {
  const context = useContext(EntityDetailTabsContext);
  if (!context) {
    throw new Error(
      'useEntityDetailTabsContext must be used within EntityDetailTabsProvider',
    );
  }
  return context;
}

function OutboundEmailContent({ email }: { email: OutboundEmail }) {
  const sentAtFormatted = email.sentAt
    ? format(new Date(email.sentAt), 'MMM d, yyyy, h:mm a')
    : null;
  return (
    <div className="flex flex-col h-full overflow-y-auto px-6 py-6 space-y-4">
      <div className="flex items-center gap-2">
        <EnvelopeSimpleIcon className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-sm font-semibold">{email.subject}</h3>
      </div>
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">
          <T>From</T>
        </span>
        <span className="truncate">{email.from}</span>
        <span className="text-muted-foreground">
          <T>To</T>
        </span>
        <span className="truncate">{email.to}</span>
        {email.cc && (
          <>
            <span className="text-muted-foreground">
              <T>CC</T>
            </span>
            <span className="truncate">{email.cc}</span>
          </>
        )}
        <span className="text-muted-foreground">
          <T>Status</T>
        </span>
        <span>{email.status}</span>
        {sentAtFormatted && (
          <>
            <span className="text-muted-foreground">
              <T>Sent</T>
            </span>
            <span>{sentAtFormatted}</span>
          </>
        )}
      </div>
      {email.htmlContent && (
        <iframe
          srcDoc={email.htmlContent}
          title={email.subject}
          className="w-full flex-1 min-h-[300px] rounded border"
          sandbox=""
        />
      )}
    </div>
  );
}

interface EntityDetailTabsProviderProps {
  initialEntity: EntityTab;
  children: React.ReactNode;
}

export function EntityDetailTabsProvider({
  initialEntity,
  children,
}: EntityDetailTabsProviderProps) {
  const gt = useGT();
  const initialEntityKey = useMemo(
    () => getEntityKey(initialEntity.entityType, initialEntity.entityId),
    [initialEntity.entityType, initialEntity.entityId],
  );

  const [activeTabId, setActiveTabId] = useState<string>(initialEntityKey);

  // This ref is the single source of truth for which entities we have ever seen.
  // It only ever grows — entities are added during render as query data arrives.
  // Keeping it as a ref (not state) means:
  //   1. useQueries arrays are driven by it → stable hook call counts (entities never removed)
  //   2. Mutating it during render is safe because it causes no re-render itself; the next
  //      render triggered by the query result settling is what makes new tabs appear
  const seenEntitiesRef = useRef<Map<string, EntityTab>>(
    new Map([[initialEntityKey, initialEntity]]),
  );

  // Tracks entity keys that have been explicitly removed by the user.
  // Prevents stale association query data from re-registering deleted tabs.
  const deletedKeysRef = useRef<Set<string>>(new Set());

  // Maps a conversationId → the first outbound email entity ID seen for that
  // conversation. Additional emails in the same conversation are not registered
  // as separate tabs; instead the representative's tab shows the full thread.
  const conversationRepresentativeRef = useRef<Map<string, string>>(new Map());
  // Maps outbound email entity ID → conversationId (for rendering the thread).
  const emailConversationIdRef = useRef<Map<string, string>>(new Map());

  // Entity keys surfaced from a direct FK on a parent entity (e.g. a PO's
  // salesOrderId) rather than from a core_entity_associations row. These tabs
  // can't be unlinked — there is no association row to delete — so the header
  // hides their X.
  const fkDerivedKeysRef = useRef<Set<string>>(new Set());

  const deleteAssociation = useDeleteEntityAssociation();

  /**
   * Register an entity in the seen-entities map if it hasn't been seen yet
   * and hasn't been explicitly removed.
   * Called during render — this is intentional (updating a ref, not state).
   */
  function registerEntity(entityType: SystemEntity, entityId: string) {
    const key = getEntityKey(entityType, entityId);
    if (deletedKeysRef.current.has(key)) return;
    if (!seenEntitiesRef.current.has(key)) {
      // Mutate ref in place; the new entry will be picked up on the next render
      // triggered by whichever query caused this discovery.
      seenEntitiesRef.current = new Map(seenEntitiesRef.current);
      seenEntitiesRef.current.set(key, { entityType, entityId });
    }
  }

  /**
   * Remove an entity tab by deleting the association and removing from the ref.
   * Switches to the initial entity tab if the removed entity was active.
   */
  const removeEntity = useCallback(
    async (entityType: SystemEntity, entityId: string) => {
      const key = getEntityKey(entityType, entityId);
      if (key === initialEntityKey) return; // Cannot remove the initial entity

      // Mark as deleted before mutation so stale query data can't re-register it
      deletedKeysRef.current = new Set(deletedKeysRef.current);
      deletedKeysRef.current.add(key);

      try {
        // Delete the entity association
        await deleteAssociation.mutateAsync({
          sourceEntityType: initialEntity.entityType,
          sourceEntityId: initialEntity.entityId,
          targetEntityType: entityType,
          targetEntityId: entityId,
        });

        // Remove from seen entities ref
        const newMap = new Map(seenEntitiesRef.current);
        newMap.delete(key);
        seenEntitiesRef.current = newMap;

        // Switch only if the currently active tab is the removed one
        setActiveTabId((currentTabId) =>
          currentTabId === key ? initialEntityKey : currentTabId,
        );
      } catch {
        // Roll back tombstone so the tab remains visible
        const rolled = new Set(deletedKeysRef.current);
        rolled.delete(key);
        deletedKeysRef.current = rolled;
      }
    },
    [initialEntityKey, initialEntity, deleteAssociation, setActiveTabId],
  );

  // Stable sorted snapshot of the seen entities, split by type for useQueries.
  // We sort so the hook array order is deterministic across renders.
  const seenEntitiesArray = useMemo(
    () =>
      Array.from(seenEntitiesRef.current.values()).sort((a, b) => {
        if (a.entityType !== b.entityType)
          return a.entityType.localeCompare(b.entityType);
        return a.entityId.localeCompare(b.entityId);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seenEntitiesRef.current],
  );

  const invoiceEntities = useMemo(
    () =>
      seenEntitiesArray.filter((e) => e.entityType === SystemEntity.AP_INVOICE),
    [seenEntitiesArray],
  );
  const purchaseOrderEntities = useMemo(
    () =>
      seenEntitiesArray.filter(
        (e) => e.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER,
      ),
    [seenEntitiesArray],
  );
  const paymentEntities = useMemo(
    () =>
      seenEntitiesArray.filter((e) => e.entityType === SystemEntity.PAYMENT),
    [seenEntitiesArray],
  );
  const salesOrderEntities = useMemo(
    () =>
      seenEntitiesArray.filter(
        (e) => e.entityType === SystemEntity.AR_SALES_ORDER,
      ),
    [seenEntitiesArray],
  );
  const shipmentEntities = useMemo(
    () =>
      seenEntitiesArray.filter(
        (e) => e.entityType === SystemEntity.SCM_SHIPMENT,
      ),
    [seenEntitiesArray],
  );
  const outboundEmailEntities = useMemo(
    () =>
      seenEntitiesArray.filter(
        (e) => e.entityType === SystemEntity.OUTBOUND_EMAIL,
      ),
    [seenEntitiesArray],
  );
  const arInvoiceEntities = useMemo(
    () =>
      seenEntitiesArray.filter((e) => e.entityType === SystemEntity.AR_INVOICE),
    [seenEntitiesArray],
  );

  // ── Association query (initial entity only) ─────────────────────────────────
  //
  // Only the initial entity's associations are needed for tab discovery.
  // Non-initial entities use skipToken to avoid unnecessary network requests.

  const invoiceAssociationQueries = useQueries({
    queries: invoiceEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input:
          entity.entityId === initialEntity.entityId &&
          entity.entityType === initialEntity.entityType
            ? {
                entityId: entity.entityId,
                entityType: SystemEntity.AP_INVOICE,
              }
            : skipToken,
      }),
    ),
  });

  const purchaseOrderAssociationQueries = useQueries({
    queries: purchaseOrderEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input:
          entity.entityId === initialEntity.entityId &&
          entity.entityType === initialEntity.entityType
            ? {
                entityId: entity.entityId,
                entityType: SystemEntity.INTERNAL_PURCHASE_ORDER,
              }
            : skipToken,
      }),
    ),
  });

  const paymentAssociationQueries = useQueries({
    queries: paymentEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input:
          entity.entityId === initialEntity.entityId &&
          entity.entityType === initialEntity.entityType
            ? {
                entityId: entity.entityId,
                entityType: SystemEntity.PAYMENT,
              }
            : skipToken,
      }),
    ),
  });

  const salesOrderAssociationQueries = useQueries({
    queries: salesOrderEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input:
          entity.entityId === initialEntity.entityId &&
          entity.entityType === initialEntity.entityType
            ? {
                entityId: entity.entityId,
                entityType: SystemEntity.AR_SALES_ORDER,
              }
            : skipToken,
      }),
    ),
  });

  const shipmentAssociationQueries = useQueries({
    queries: shipmentEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input:
          entity.entityId === initialEntity.entityId &&
          entity.entityType === initialEntity.entityType
            ? {
                entityId: entity.entityId,
                entityType: SystemEntity.SCM_SHIPMENT,
              }
            : skipToken,
      }),
    ),
  });

  const arInvoiceAssociationQueries = useQueries({
    queries: arInvoiceEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input:
          entity.entityId === initialEntity.entityId &&
          entity.entityType === initialEntity.entityType
            ? {
                entityId: entity.entityId,
                entityType: SystemEntity.AR_INVOICE,
              }
            : skipToken,
      }),
    ),
  });

  // ── Entity data queries ──────────────────────────────────────────────────────

  const invoiceDataQueries = useQueries({
    queries: invoiceEntities.map((entity) =>
      orpc.accountsPayable.invoices.get.queryOptions({
        input: entity.entityId ? { id: entity.entityId } : skipToken,
      }),
    ),
  });

  const purchaseOrderDataQueries = useQueries({
    queries: purchaseOrderEntities.map((entity) =>
      orpc.accountsPayable.purchaseOrders.get.queryOptions({
        input: entity.entityId ? { id: entity.entityId } : skipToken,
      }),
    ),
  });

  const paymentDataQueries = useQueries({
    queries: paymentEntities.map((entity) =>
      orpc.finance.payments.get.queryOptions({
        input: entity.entityId
          ? { id: entity.entityId, includeProviderDetails: true }
          : skipToken,
      }),
    ),
  });

  const salesOrderDataQueries = useQueries({
    queries: useMemo(
      () =>
        salesOrderEntities.map((entity) => ({
          ...orpc.accountsReceivable.salesOrders.get.queryOptions({
            input: entity.entityId ? { id: entity.entityId } : skipToken,
          }),
          staleTime: 30_000,
        })),
      [salesOrderEntities],
    ),
  });

  const shipmentDataQueries = useQueries({
    queries: useMemo(
      () =>
        shipmentEntities.map((entity) => ({
          ...orpc.supplyChain.shipments.get.queryOptions({
            input: entity.entityId ? { id: entity.entityId } : skipToken,
          }),
          staleTime: 30_000,
        })),
      [shipmentEntities],
    ),
  });

  const outboundEmailDataQueries = useQueries({
    queries: outboundEmailEntities.map((entity) =>
      orpc.communications.outboundEmails.get.queryOptions({
        input: entity.entityId ? { id: entity.entityId } : skipToken,
      }),
    ),
  });

  const arInvoiceDataQueries = useQueries({
    queries: arInvoiceEntities.map((entity) =>
      orpc.accountsReceivable.arInvoices.get.queryOptions({
        input: entity.entityId ? { id: entity.entityId } : skipToken,
      }),
    ),
  });

  // ── Discover entities explicitly linked to the initial entity ────────────────
  //
  // Only show entities that are directly associated with the initial entity
  // (no graph fanout to associations of associated entities).
  // Additionally, show sales orders for any shipment tabs.

  // Find and process only the initial entity's association query
  const initialAssocQuery = (() => {
    const typeToQueries: Record<
      string,
      { entities: EntityTab[]; queries: typeof invoiceAssociationQueries }
    > = {
      [SystemEntity.AP_INVOICE]: {
        entities: invoiceEntities,
        queries: invoiceAssociationQueries,
      },
      [SystemEntity.INTERNAL_PURCHASE_ORDER]: {
        entities: purchaseOrderEntities,
        queries: purchaseOrderAssociationQueries,
      },
      [SystemEntity.PAYMENT]: {
        entities: paymentEntities,
        queries: paymentAssociationQueries,
      },
      [SystemEntity.AR_SALES_ORDER]: {
        entities: salesOrderEntities,
        queries: salesOrderAssociationQueries,
      },
      [SystemEntity.SCM_SHIPMENT]: {
        entities: shipmentEntities,
        queries: shipmentAssociationQueries,
      },
      [SystemEntity.AR_INVOICE]: {
        entities: arInvoiceEntities,
        queries: arInvoiceAssociationQueries,
      },
    };
    const entry = typeToQueries[initialEntity.entityType];
    if (!entry) return undefined;
    const idx = entry.entities.findIndex(
      (e) => e.entityId === initialEntity.entityId,
    );
    return idx !== -1 ? entry.queries[idx] : undefined;
  })();

  if (initialAssocQuery?.data?.associations) {
    // Rebuild conversation maps from current data on each render so that if the
    // representative email is removed, remaining emails in the conversation can
    // elect a new representative instead of the thread disappearing.
    conversationRepresentativeRef.current = new Map();
    emailConversationIdRef.current = new Map();

    // Prune tombstones for keys no longer in the server's association list.
    // Once the refetch confirms an entity was removed, the tombstone is no
    // longer needed and the entity can be re-linked in the same session.
    if (deletedKeysRef.current.size > 0) {
      const currentAssocKeys = new Set(
        initialAssocQuery.data.associations
          .filter((assoc) => isRelevantEntityType(assoc.entity.entityType))
          .map((assoc) =>
            getEntityKey(assoc.entity.entityType, assoc.entity.entityId),
          ),
      );
      const pruned = new Set(
        Array.from(deletedKeysRef.current).filter((key) =>
          currentAssocKeys.has(key),
        ),
      );
      if (pruned.size !== deletedKeysRef.current.size) {
        deletedKeysRef.current = pruned;
      }
    }

    for (const assoc of initialAssocQuery.data.associations) {
      if (!isRelevantEntityType(assoc.entity.entityType)) continue;

      if (assoc.entity.entityType === SystemEntity.OUTBOUND_EMAIL) {
        const conversationId = (
          assoc.metadata as { conversationId?: string } | null | undefined
        )?.conversationId;

        if (conversationId) {
          if (!conversationRepresentativeRef.current.has(conversationId)) {
            // First email in this conversation — make it the representative tab
            conversationRepresentativeRef.current.set(
              conversationId,
              assoc.entity.entityId,
            );
            emailConversationIdRef.current.set(
              assoc.entity.entityId,
              conversationId,
            );
            registerEntity(assoc.entity.entityType, assoc.entity.entityId);
          }
          // Subsequent emails in the same conversation are intentionally skipped
          continue;
        }
      }

      registerEntity(assoc.entity.entityType, assoc.entity.entityId);
    }
  }

  // ── Discover the sales order linked to each loaded purchase order ─────────────
  //
  // A PO references its sales order through a direct salesOrderId FK, not a
  // core_entity_associations row, so the association discovery above won't find
  // it. Register it explicitly here so a linked sales order surfaces as its own
  // tab. Marked FK-derived so the header doesn't offer a (no-op) unlink action.
  purchaseOrderEntities.forEach((_purchaseOrder, index) => {
    const salesOrderId =
      purchaseOrderDataQueries[index]?.data?.purchaseOrder?.salesOrderId;
    if (!salesOrderId) return;
    registerEntity(SystemEntity.AR_SALES_ORDER, salesOrderId);
    fkDerivedKeysRef.current.add(
      getEntityKey(SystemEntity.AR_SALES_ORDER, salesOrderId),
    );
  });

  // ── Build data maps for renderEntityContent ──────────────────────────────────

  const invoiceDataMap = useMemo(() => {
    const map = new Map<string, (typeof invoiceDataQueries)[0]>();
    invoiceEntities.forEach((entity, index) => {
      map.set(
        getEntityKey(entity.entityType, entity.entityId),
        invoiceDataQueries[index],
      );
    });
    return map;
  }, [invoiceEntities, invoiceDataQueries]);

  const purchaseOrderDataMap = useMemo(() => {
    const map = new Map<string, (typeof purchaseOrderDataQueries)[0]>();
    purchaseOrderEntities.forEach((entity, index) => {
      map.set(
        getEntityKey(entity.entityType, entity.entityId),
        purchaseOrderDataQueries[index],
      );
    });
    return map;
  }, [purchaseOrderEntities, purchaseOrderDataQueries]);

  const paymentDataMap = useMemo(() => {
    const map = new Map<string, (typeof paymentDataQueries)[0]>();
    paymentEntities.forEach((entity, index) => {
      map.set(
        getEntityKey(entity.entityType, entity.entityId),
        paymentDataQueries[index],
      );
    });
    return map;
  }, [paymentEntities, paymentDataQueries]);

  const salesOrderDataMap = useMemo(() => {
    const map = new Map<string, (typeof salesOrderDataQueries)[0]>();
    salesOrderEntities.forEach((entity, index) => {
      map.set(
        getEntityKey(entity.entityType, entity.entityId),
        salesOrderDataQueries[index],
      );
    });
    return map;
  }, [salesOrderEntities, salesOrderDataQueries]);

  const shipmentDataMap = useMemo(() => {
    const map = new Map<string, (typeof shipmentDataQueries)[0]>();
    shipmentEntities.forEach((entity, index) => {
      map.set(
        getEntityKey(entity.entityType, entity.entityId),
        shipmentDataQueries[index],
      );
    });
    return map;
  }, [shipmentEntities, shipmentDataQueries]);

  const outboundEmailDataMap = useMemo(() => {
    const map = new Map<string, (typeof outboundEmailDataQueries)[0]>();
    outboundEmailEntities.forEach((entity, index) => {
      map.set(
        getEntityKey(entity.entityType, entity.entityId),
        outboundEmailDataQueries[index],
      );
    });
    return map;
  }, [outboundEmailEntities, outboundEmailDataQueries]);

  const arInvoiceDataMap = useMemo(() => {
    const map = new Map<string, (typeof arInvoiceDataQueries)[0]>();
    arInvoiceEntities.forEach((entity, index) => {
      map.set(
        getEntityKey(entity.entityType, entity.entityId),
        arInvoiceDataQueries[index],
      );
    });
    return map;
  }, [arInvoiceEntities, arInvoiceDataQueries]);

  // ── entityArray: what the tab bar renders ────────────────────────────────────
  //
  // Derived directly from seenEntitiesRef during render — no state, no effect.
  // Initial entity always comes first; the rest are sorted for stability.

  const entityArray = useMemo(() => {
    return Array.from(seenEntitiesRef.current.values()).sort((a, b) => {
      const aKey = getEntityKey(a.entityType, a.entityId);
      const bKey = getEntityKey(b.entityType, b.entityId);
      if (aKey === initialEntityKey) return -1;
      if (bKey === initialEntityKey) return 1;
      if (a.entityType !== b.entityType)
        return a.entityType.localeCompare(b.entityType);
      return a.entityId.localeCompare(b.entityId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seenEntitiesRef.current, initialEntityKey]);

  // ── Render entity detail content ─────────────────────────────────────────────

  // Each branch gates on `isPending`, not `isLoading`: on the first render the
  // observer has not started fetching yet, so `isLoading` (pending AND
  // fetching) is false while `data` is still undefined, and the error branch
  // would flash before the content arrived.
  function renderEntityContent(entity: EntityTab) {
    const key = getEntityKey(entity.entityType, entity.entityId);

    if (entity.entityType === SystemEntity.AP_INVOICE) {
      const query = invoiceDataMap.get(key);
      if (!query) {
        return <LoadingSpinner />;
      }
      const { data: invoiceData, isPending, isError } = query;
      if (isPending) {
        return <LoadingSpinner />;
      }
      if (isError || !invoiceData?.invoice) {
        return <ErrorMessage message={gt('Unable to load invoice details')} />;
      }
      return (
        <InvoiceDetails
          invoice={invoiceData.invoice}
          inboundEmail={invoiceData.inboundEmail}
          lineItems={invoiceData.lineItems}
          relatedConversations={invoiceData.relatedConversations}
          extractedTotalWarning={invoiceData.extractedTotalWarning}
        />
      );
    } else if (entity.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER) {
      const query = purchaseOrderDataMap.get(key);
      if (!query) {
        return <LoadingSpinner />;
      }
      const { data: purchaseOrderData, isPending, isError } = query;
      if (isPending) {
        return <LoadingSpinner />;
      }
      if (isError || !purchaseOrderData?.purchaseOrder) {
        return (
          <ErrorMessage message={gt('Unable to load purchase order details')} />
        );
      }
      return (
        <PurchaseOrderDetails
          purchaseOrder={purchaseOrderData.purchaseOrder}
          lineItems={purchaseOrderData.lineItems || []}
        />
      );
    } else if (entity.entityType === SystemEntity.PAYMENT) {
      const query = paymentDataMap.get(key);
      if (!query) {
        return <PaymentDetailsSkeleton />;
      }
      const { data: payment, isPending, isError } = query;
      if (isPending) {
        return <PaymentDetailsSkeleton />;
      }
      if (isError || !payment) {
        return <ErrorMessage message={gt('Unable to load payment details')} />;
      }
      return <PaymentDetails key={payment.id} payment={payment} />;
    } else if (entity.entityType === SystemEntity.AR_SALES_ORDER) {
      const query = salesOrderDataMap.get(key);
      if (!query) {
        return <LoadingSpinner />;
      }
      const { data: salesOrderData, isPending, isError } = query;
      if (isPending) {
        return <LoadingSpinner />;
      }
      if (isError || !salesOrderData?.salesOrder) {
        return (
          <ErrorMessage message={gt('Unable to load sales order details')} />
        );
      }
      return (
        <SalesOrderDetails
          salesOrder={salesOrderData.salesOrder}
          lineItems={salesOrderData.lineItems || []}
        />
      );
    } else if (entity.entityType === SystemEntity.SCM_SHIPMENT) {
      const query = shipmentDataMap.get(key);
      if (!query) {
        return <LoadingSpinner />;
      }
      const { data: shipmentData, isPending, isError } = query;
      if (isPending) {
        return <LoadingSpinner />;
      }
      if (isError || !shipmentData?.shipment) {
        return <ErrorMessage message={gt('Unable to load shipment details')} />;
      }
      return (
        <ShipmentDetails
          shipment={shipmentData.shipment}
          salesOrders={shipmentData.salesOrders || []}
          purchaseOrders={shipmentData.purchaseOrders || []}
          orderedDestinations={shipmentData.orderedDestinations || []}
          chargeBreakdowns={shipmentData.chargeBreakdowns || []}
          chargesCount={shipmentData.chargesCount || 0}
        />
      );
    }

    if (entity.entityType === SystemEntity.AR_INVOICE) {
      const query = arInvoiceDataMap.get(key);
      if (!query) {
        return <LoadingSpinner />;
      }
      const { data: arInvoiceData, isPending, isError } = query;
      if (isPending) {
        return <LoadingSpinner />;
      }
      if (isError || !arInvoiceData?.invoice) {
        return <ErrorMessage message={gt('Unable to load invoice details')} />;
      }
      return <ARInvoiceDetails invoice={arInvoiceData.invoice} />;
    } else if (entity.entityType === SystemEntity.AR_CASH_RECEIPT) {
      return (
        <CashReceiptDetailView
          receiptId={entity.entityId}
          hideActions
          hideTaskCard
        />
      );
    }

    if (entity.entityType === SystemEntity.OUTBOUND_EMAIL) {
      const conversationId = emailConversationIdRef.current.get(
        entity.entityId,
      );
      if (conversationId) {
        return (
          <div className="h-full overflow-auto">
            <EmailThreadViewer conversationId={conversationId} />
          </div>
        );
      }

      const query = outboundEmailDataMap.get(key);
      if (!query) {
        return <LoadingSpinner />;
      }
      const { data: email, isPending, isError } = query;
      if (isPending) {
        return <LoadingSpinner />;
      }
      if (isError || !email) {
        return <ErrorMessage message={gt('Unable to load email details')} />;
      }
      return <OutboundEmailContent email={email} />;
    }

    return (
      <div className="p-4 text-sm text-muted-foreground">
        <T>Unknown entity type</T>
      </div>
    );
  }

  function isConversationTab(entityType: SystemEntity, entityId: string) {
    return (
      entityType === SystemEntity.OUTBOUND_EMAIL &&
      emailConversationIdRef.current.has(entityId)
    );
  }

  function isFkDerivedTab(entityType: SystemEntity, entityId: string) {
    return fkDerivedKeysRef.current.has(getEntityKey(entityType, entityId));
  }

  // AR invoice tabs are derived from FKs on read, not persisted association
  // rows, so they can't be individually unlinked.
  const associationsAreDerived =
    initialEntity.entityType === SystemEntity.AR_INVOICE;

  const contextValue: EntityDetailTabsContextValue = {
    entityArray,
    activeTabId,
    setActiveTabId,
    renderEntityContent,
    initialEntityKey,
    removeEntity,
    isRemovingEntity: deleteAssociation.isPending,
    isConversationTab,
    isFkDerivedTab,
    associationsAreDerived,
  };

  return (
    <EntityDetailTabsContext.Provider value={contextValue}>
      {children}
    </EntityDetailTabsContext.Provider>
  );
}

export function EntityDetailTabsHeader() {
  const gt = useGT();
  const {
    entityArray,
    initialEntityKey,
    removeEntity,
    isRemovingEntity,
    isConversationTab,
    isFkDerivedTab,
    associationsAreDerived,
  } = useEntityDetailTabsContext();

  if (entityArray.length === 0) {
    return null;
  }

  return (
    <TabsList className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
      {entityArray.map((entity) => {
        const key = getEntityKey(entity.entityType, entity.entityId);
        const isInitialEntity = key === initialEntityKey;
        return (
          <TabsTrigger
            key={key}
            value={key}
            className={cn(
              'group',
              isInitialEntity && 'font-semibold data-[active]:text-primary',
            )}
          >
            <SystemEntityInline
              entityType={entity.entityType}
              entityId={entity.entityId}
            />
            {!isInitialEntity &&
              !associationsAreDerived &&
              !isConversationTab(entity.entityType, entity.entityId) &&
              !isFkDerivedTab(entity.entityType, entity.entityId) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-1 h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
                  disabled={isRemovingEntity}
                  onClick={(e) => {
                    e.stopPropagation();
                    void removeEntity(entity.entityType, entity.entityId);
                  }}
                  aria-label={gt('Remove linked entity', {
                    context: 'button to remove this linked entity tab',
                  })}
                >
                  <XIcon className="h-3 w-3" />
                </Button>
              )}
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}

export function EntityDetailTabsContent() {
  const { entityArray, renderEntityContent } = useEntityDetailTabsContext();

  if (entityArray.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        <T>No entities found</T>
      </div>
    );
  }

  return (
    <div className="h-full">
      {entityArray.map((entity) => {
        const key = getEntityKey(entity.entityType, entity.entityId);
        return (
          <TabsContent
            key={key}
            value={key}
            className="mt-0 h-full not-data-[hidden]:flex not-data-[hidden]:flex-col"
          >
            {renderEntityContent(entity)}
          </TabsContent>
        );
      })}
    </div>
  );
}

interface EntityDetailTabsProps {
  initialEntity: EntityTab;
}

export function EntityDetailTabs({ initialEntity }: EntityDetailTabsProps) {
  return (
    <EntityDetailTabsProvider initialEntity={initialEntity}>
      <EntityDetailTabsInner />
    </EntityDetailTabsProvider>
  );
}

function EntityDetailTabsInner() {
  const { activeTabId, setActiveTabId } = useEntityDetailTabsContext();
  return (
    <Tabs
      value={activeTabId}
      onValueChange={setActiveTabId}
      className="flex flex-col h-full"
    >
      <EntityDetailTabsHeader />
      <EntityDetailTabsContent />
    </Tabs>
  );
}
